<template>
  <sf-modal
    v-if="modalOpen && !isOnCompanySelection"
    size="l"
    class="sf-border-box"
    data-walkthrough-id="account-entries-modal"
    inner-modal-classes="sf-overflow-hidden"
    :footer-padding="0"
    :scrollable-content="true"
    @close="close"
  >
    <template #title>{{
      external
        ? I18n.t('company_links.select_external_accounts')
        : I18n.t('account_selector_modal.select_account')
    }}</template>

    <template #body>
      <div v-if="isLoading" class="sf-pad-3 sf-level sf-level-center">
        <div class="sf-spinner"></div>
      </div>
      <div v-else class="sf-grid sf-grid-fluid sf-grid-gutter-medium">
        <div v-if="isCompanySelected" class="sf-row sf-layer-border-bottom">
          <SelectedExternalCompany :ledger-label="ledgerLabel" />
        </div>
        <div class="sf-row">
          <div class="sf-col sf-sm-6">
            <div class="sf-level sf-level-between sf-below-1 sf-hover">
              <div class="sf-uppercase sf-pad-l-3">
                {{ I18n.t('account_selector_modal.selected_accounts') }}
              </div>
            </div>
            <div
              class="sf-tab sf-overflow-scroll"
              style="
                height: calc(-308px + 100vh);
                min-height: 452px;
                max-height: 652px;
              "
              data-testid="selected-accounts-tab"
            >
              <div
                v-for="account in selectedAccounts"
                :key="account.id"
                class="sf-tab-item sf-level sf-hover"
                data-test="selected-account"
              >
                <div class="fa fa-check sf-c-success"></div>
                <div class="sf-level sf-level-between sf-full">
                  <div>
                    <div
                      v-if="showOriginalName(account)"
                      class="sf-c-text-light sf-below-1"
                    >
                      {{ account.originalNumberWithOriginalName }}
                    </div>
                    <span class="sf-fwdb sf-c-text-dark">{{
                      account.numberWithName
                    }}</span>
                  </div>
                  <SfButton
                    variant=""
                    :small="true"
                    class="sf-hover-item"
                    @click="deselectAccount(account)"
                    >{{ I18n.t('common.remove') }}</SfButton
                  >
                </div>
              </div>
            </div>
          </div>
          <div class="sf-col sf-sm-6">
            <div class="sf-level sf-level-between sf-below-1">
              <div class="sf-uppercase sf-pad-l-3">
                {{ I18n.t('account_selector_modal.available_accounts') }}
              </div>
            </div>
            <div
              class="
                sf-level
                sf-below-2
                sf-layer-rounded
                sf-bg-snow
                sf-pad-v-1
                sf-pad-h-2
              "
            >
              <input
                v-model="searchText"
                class="sf-form-input-transparent sf-full"
                data-test="search-text"
                placeholder="Filter"
                type="text"
              />
              <SfButton variant="" :small="true" @click="selectFiltered">{{
                I18n.t('account_selector_modal.select_all')
              }}</SfButton>
            </div>
            <div
              class="sf-tab sf-overflow-scroll"
              style="
                height: calc(-360px + 100vh);
                min-height: 400px;
                max-height: 600px;
              "
            >
              <div
                v-for="account in filteredAccounts"
                :key="account.id"
                class="sf-tab-item sf-level"
                data-test="available-account"
              >
                <div class="sf-level sf-level-between sf-full">
                  <div>
                    <div
                      v-if="showOriginalName(account)"
                      class="sf-c-text-light sf-below-1"
                    >
                      {{ account.originalNumberWithOriginalName }}
                    </div>
                    <span class="sf-fwdb sf-c-text">{{
                      account.numberWithName
                    }}</span>
                  </div>
                  <SfButton
                    variant=""
                    :small="true"
                    @click="selectAccount(account)"
                    >{{ I18n.t('common.select') }}
                  </SfButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="sf-pad-h-4 sf-pad-v-2 tw-w-full">
        <div class="sf-level sf-level-between">
          <div class="sf-level">
            <span
              v-if="isDefaultChanged"
              class="sf-c-warning fa fa-warning"
            ></span>
            <div v-if="isDefaultChanged" class="sf-c-warning-darker">
              {{ I18n.t('account_selector_modal.changed_from_default') }}
            </div>
            <SfButton
              v-if="isDefaultChanged"
              variant=""
              :small="true"
              @click="restoreDefault"
              >{{ I18n.t('account_selector_modal.restore_default') }}</SfButton
            >
            <SfButton
              v-if="canDeselect"
              :small="true"
              variant=""
              @click="deselectAll"
              ><span>{{
                I18n.t('account_selector_modal.clear_all')
              }}</span></SfButton
            >
          </div>
          <div class="sf-level">
            <SfButton variant="" @click="close">{{
              I18n.t('common.form.cancel')
            }}</SfButton>
            <SfButton
              variant=""
              :class="isOnCompanySelection ? 'sf-disabled' : 'sf-btn-success'"
              @click="save"
              >{{ I18n.t('common.save') }}</SfButton
            >
          </div>
        </div>
      </div>
    </template>
  </sf-modal>
</template>

<script>
import api from '../../../../templates/api/index'
import { doesAccountNameSatisfyQuery } from '../../../../utils/account-search-helpers'
import { SfModal } from '@app/shared/vue/components/sf-modal'
import _ from 'lodash'
import { SfLog } from '@app/adapters/logger'
import SfButton from '@app/shared/vue/components/sf-button/sf-button.vue'
import SelectedExternalCompany from '@app/application/company_links/components/selected-external-company'
import { useExternalCompaniesStore } from '@app/application/company_links/store/use-external-companies'
import { mapState as mapPiniaState, mapActions as mapPiniaActions } from 'pinia'

export default {
  components: {
    SfModal,
    SfButton,
    SelectedExternalCompany,
  },

  data() {
    return {
      modalOpen: true,
      isLoading: false,
      selectedAccounts: [],
      otherAccounts: [],
      defaultAccounts: [],
      updatePath: null,
      afterUpdate: null,
      updateParams: {},
      searchText: '',
      range: null,
      defaultValue: null,
      value: null,
      external: false,
      ledgerLabel: null,
    }
  },

  computed: {
    ...mapPiniaState(useExternalCompaniesStore, ['selectedCompany']),

    valueToSubmit() {
      if (this.defaultAccounts.length && !this.isDefaultChanged) {
        return ''
      } else if (this.selectedAccounts.length) {
        return this.selectedAccounts.map((value) => `#${value.id}`).join(',')
      } else {
        return '~!'
      }
    },

    filteredAccounts() {
      const accountsToShow = this.searchText
        ? this.otherAccounts.filter((account) =>
            this.isAccountSatisfyQuery(account, this.searchText)
          )
        : this.otherAccounts

      return _.sortBy(accountsToShow, (acc) => acc.numberWithName)
    },

    isDefaultChanged() {
      const hasDifferentItems =
        this.selectedAccounts?.length !== this.defaultAccounts?.length ||
        _.differenceWith(
          this.selectedAccounts,
          this.defaultAccounts,
          this.compareAccount
        ).length !== 0

      return this.defaultAccounts?.length && hasDifferentItems
    },

    isOnCompanySelection() {
      return this.external && !this.selectedCompany
    },

    isCompanySelected() {
      return this.external && this.selectedCompany
    },

    canDeselect() {
      return this.selectedAccounts?.length > 0 && !this.isOnCompanySelection
    },
  },

  watch: {
    selectedCompany: {
      immediate: true,
      deep: true,
      handler(newValue) {
        if (!this.external) return

        if (newValue && newValue.id !== this.lastSelectedCompanyId) {
          this.lastSelectedCompanyId = newValue.id
          this.handleCompanySelected(newValue.id)
          window.modalCitizen.closeModalPortal(
            'external-company-selector-modal'
          )
        }
      },
    },
  },

  methods: {
    ...mapPiniaActions(useExternalCompaniesStore, [
      'clearSelectedCompanies',
      'setSelectedCompaniesById',
    ]),

    openModal({
      defaultValue,
      value,
      attemptToConfirmUnreview,
      range,
      updatePath,
      afterUpdate,
      objectLedgerId,
      external,
      ledgerLabel,
      dataToSubmit,
      ...updateParams
    }) {
      this.attemptToConfirmUnreview = attemptToConfirmUnreview
      this.updatePath = updatePath
      this.updateParams = updateParams
      this.afterUpdate = afterUpdate
      this.objectLedgerId = objectLedgerId
      this.range = range
      this.defaultValue = defaultValue
      this.value = value
      this.modalOpen = true
      this.isLoading = true
      this.external = external
      this.ledgerLabel = ledgerLabel
      this.dataToSubmit = dataToSubmit

      if (external) {
        this.fetchExternalSelectorAccounts()
      } else {
        this.fetchSelectorAccounts()
      }
    },

    async fetchSelectorAccounts() {
      this.isLoading = true

      try {
        const response = await api.getSelectorModalAccounts({
          range: this.range,
          defaultValue: this.defaultValue,
          value: this.value,
          objectLedgerId: this.objectLedgerId,
        })

        const { accounts, selectedAccounts, defaultAccounts } = response || {}
        this.selectedAccounts = selectedAccounts
        this.otherAccounts = _.differenceWith(
          accounts,
          this.selectedAccounts,
          this.compareAccount
        )
        this.defaultAccounts = defaultAccounts
        this.isLoading = false
      } catch (e) {
        SfLog.warn('Error while fetching accounts for the selector', {
          extra: {
            error: e,
          },
        })
      }
    },

    async fetchExternalSelectorAccounts(newlySelectedCompanyId = null) {
      this.isLoading = true

      try {
        const response = await api.getExternalSelectorModalAccounts({
          range: this.range,
          value: this.value,
          objectLedgerId: this.objectLedgerId,
          selectedCompanyId: newlySelectedCompanyId,
        })

        const {
          accounts,
          selectedAccounts,
          defaultAccounts,
          selectedCompanyId,
        } = response || {}

        this.selectedAccounts = selectedAccounts
        this.otherAccounts = _.differenceWith(
          accounts,
          this.selectedAccounts,
          this.compareAccount
        )
        this.defaultAccounts = defaultAccounts
        if (selectedCompanyId) {
          this.setSelectedCompaniesById([selectedCompanyId])
        } else {
          window.modalCitizen.openModalPortal('external-company-selector-modal')
        }
        this.isLoading = false
      } catch (e) {
        SfLog.warn('Error while fetching external accounts for the selector', {
          extra: {
            error: e,
          },
        })
      }
    },

    close() {
      if (this.external) {
        window.modalCitizen.closeModalPortal('external-company-selector-modal')
        this.clearSelectedCompanies()
      }

      this.modalOpen = false
      this.searchText = ''
    },

    save() {
      // attemptToConfirmUnreview is used to skip the confirmation when saving
      // data on the proposal edit page
      if (this.attemptToConfirmUnreview) {
        this.saveWithUnreviewConfirmation()
      } else {
        return this.performSave()
      }
    },

    saveWithUnreviewConfirmation() {
      window.eventHub.$emit('confirmUnreviewTemplate', {
        callback: () => {
          this.performSave()
        },
      })
    },

    performSave() {
      const properties = {
        properties: [
          {
            ...this.updateParams,
            objectLedgerId: this.objectLedgerId,
            value: this.valueToSubmit,
          },
          ...this.dataToSubmit,
        ],
      }

      this.isLoading = true
      api
        .updateTextProperties({ updatePath: this.updatePath, properties })
        .then((response) => {
          Silverfin.Spinner.success()
          this.close()
          this.afterUpdate(response)
          window.eventHub.$emit('reloadWorkflowReviewStatusData')
        })
        .catch(() => {
          Silverfin.Spinner.error()
        })
        .then(() => {
          this.isLoading = false
        })
    },
    selectFiltered() {
      this.filteredAccounts.forEach(this.selectAccount)
      this.searchText = ''
    },

    deselectAll() {
      // Since we're mutating selectedAccounts we need to create new array out of it first
      ;[...this.selectedAccounts].forEach(this.deselectAccount)
    },

    restoreDefault() {
      const accountsToDeselect = _.differenceWith(
        this.selectedAccounts,
        this.defaultAccounts,
        this.compareAccount
      )
      const accountsToSelect = _.differenceWith(
        this.defaultAccounts,
        this.selectedAccounts,
        this.compareAccount
      )

      accountsToDeselect.forEach(this.deselectAccount)
      accountsToSelect.forEach(this.selectAccount)
    },

    selectAccount(account) {
      const accountIndex = this.otherAccounts.indexOf(account)
      this.otherAccounts.splice(accountIndex, 1)
      this.selectedAccounts.push(account)
    },

    deselectAccount(account) {
      const accountIndex = this.selectedAccounts.indexOf(account)
      this.selectedAccounts.splice(accountIndex, 1)
      this.otherAccounts.push(account)
    },

    showOriginalName(account) {
      return account.originalNumberWithOriginalName !== account.numberWithName
    },

    compareAccount(account, otherAccount) {
      return account.id === otherAccount.id
    },

    isAccountSatisfyQuery(account, query) {
      return (
        doesAccountNameSatisfyQuery(account.numberWithName, query) ||
        doesAccountNameSatisfyQuery(
          account.originalNumberWithOriginalName,
          query
        )
      )
    },

    handleCompanySelected(companyId) {
      this.fetchExternalSelectorAccounts(companyId)
    },
  },
}
</script>
